import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { Mail, CheckCircle2, AlertTriangle, RefreshCw, Search, Copy } from "lucide-react";

// Director-facing audit trail of every email the platform tried to send for
// this production. Rows can be filtered by delivery state and searched by
// recipient or subject. This is the "email audit" the user asked for so
// producers can spot blocked / bouncing offers before they hit rehearsal.
const KINDS = {
  audition_signup: { label: "Signup confirmation", tint: "text-sky-300 bg-sky-500/10 border-sky-500/30" },
  casting_cast:    { label: "Cast offer",         tint: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" },
  casting_reject:  { label: "Rejection",          tint: "text-zinc-400 bg-white/5 border-white/10" },
  casting_welcome: { label: "Welcome",            tint: "text-gold bg-gold/10 border-gold/30" },
  casting_nudge:   { label: "Reminder nudge",     tint: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
  decline_notify:  { label: "Decline alert",      tint: "text-rose-300 bg-rose-500/10 border-rose-500/30" },
  other:           { label: "Other",              tint: "text-zinc-400 bg-white/5 border-white/10" },
};

export default function EmailLog() {
  const { production } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("all");      // all | delivered | failed
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    if (!production?.id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ status, kind });
      const { data } = await api.get(`/email-log?${params.toString()}`);
      setRows(data.rows || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't load email log"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [production?.id, status, kind]);

  const filtered = useMemo(() => {
    const term = q.toLowerCase().trim();
    if (!term) return rows;
    return rows.filter((r) => (r.to || "").toLowerCase().includes(term)
      || (r.subject || "").toLowerCase().includes(term)
      || (r.recipient_name || "").toLowerCase().includes(term));
  }, [rows, q]);

  const stats = useMemo(() => {
    const s = { total: rows.length, delivered: 0, failed: 0 };
    rows.forEach((r) => { if (r.delivered) s.delivered++; else s.failed++; });
    return s;
  }, [rows]);

  const copyRow = async (r) => {
    const text = `To: ${r.to}\nSubject: ${r.subject}\nSent: ${r.created_at}\nStatus: ${r.delivered ? "Delivered" : "FAILED — " + (r.error || "unknown")}`;
    try { await navigator.clipboard.writeText(text); toast.success("Row copied"); }
    catch { window.prompt("Copy:", text); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-8 space-y-6" data-testid="email-log-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Communications</div>
        <h1 className="mt-1 font-display text-3xl md:text-4xl leading-tight">Email audit</h1>
        <p className="mt-2 text-sm text-zinc-400 max-w-2xl">Every message this production has attempted to send — cast offers, rejections, welcome notes, reminders — with delivery status pulled straight from the provider. Anything marked <span className="text-red-300 font-medium">FAILED</span> never reached the recipient; use Copy invite on the Auditions page to send it manually.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 border border-white/10 rounded-md p-1 bg-[#121212]">
          {["all", "delivered", "failed"].map((k) => (
            <button key={k} data-testid={`emaillog-status-${k}`} onClick={() => setStatus(k)}
              className={`h-8 px-3 rounded text-xs capitalize ${status === k ? "bg-gold text-black font-semibold" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
              {k}{k === "failed" && stats.failed > 0 ? ` · ${stats.failed}` : ""}
            </button>
          ))}
        </div>
        <select data-testid="emaillog-kind" value={kind} onChange={(e) => setKind(e.target.value)}
          className="h-9 px-2 rounded-md border border-white/10 bg-[#121212] text-sm text-zinc-200">
          <option value="all">All types</option>
          {Object.entries(KINDS).filter(([k]) => k !== "other").map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <div className="relative flex-1 min-w-[220px] max-w-xs">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="emaillog-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search recipient or subject…"
            className="w-full h-9 pl-8 pr-3 rounded-md border border-white/10 bg-[#121212] text-sm text-zinc-200 placeholder:text-zinc-600" />
        </div>
        <button data-testid="emaillog-refresh" onClick={load}
          className="h-9 w-9 rounded-md border border-white/10 grid place-items-center text-zinc-400 hover:text-white hover:bg-white/5">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
        <div className="ml-auto text-[11px] text-zinc-500">
          {stats.total} total · <span className="text-emerald-300">{stats.delivered} delivered</span> · <span className="text-red-300">{stats.failed} failed</span>
        </div>
      </div>

      <div className="rounded-lg border border-white/10 bg-[#121212] overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-zinc-500">
            {loading ? "Loading…" : "No emails match this filter yet."}
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {filtered.map((r) => {
              const meta = KINDS[r.kind] || KINDS.other;
              return (
                <li key={r.id} data-testid={`emaillog-row-${r.id}`} className="p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-4 hover:bg-white/[0.02]">
                  <div className="w-6 shrink-0">
                    {r.delivered ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-red-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${meta.tint}`}>{meta.label}</span>
                      <span className="font-medium text-zinc-100 truncate">{r.subject}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                      <Mail className="w-3 h-3" />
                      <span className="text-zinc-400">{r.to}</span>
                      {r.recipient_name && <span className="text-zinc-600">· {r.recipient_name}</span>}
                      <span className="text-zinc-600">· {fmtDate(r.created_at)} {(r.created_at || "").slice(11, 16)}</span>
                      {r.sent_by_name && <span className="text-zinc-600">· by {r.sent_by_name}</span>}
                    </div>
                    {!r.delivered && r.error && (
                      <div data-testid={`emaillog-error-${r.id}`} className="mt-1.5 text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2 py-1 inline-block">
                        <AlertTriangle className="w-3 h-3 inline mr-1" /> {r.error}
                      </div>
                    )}
                  </div>
                  <button data-testid={`emaillog-copy-${r.id}`} onClick={() => copyRow(r)}
                    className="shrink-0 h-8 px-2 rounded border border-white/10 text-zinc-400 hover:text-white hover:bg-white/5 text-[11px] inline-flex items-center gap-1">
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
